import React, { Fragment, useEffect } from "react";
import { logClientEvent } from "utils/client-log.util";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import "react-toastify/dist/ReactToastify.css";

import { setupInterceptorsTo } from "lib/axios";
import { logoutUser } from "context/features/authSlice";

import SplashScreen from "components/layout/SplashScreen";
import Layout from "components/layout/Layout";
import ProfileLayout from "components/layout/ProfileLayout";
import CollectionsLayout from "components/layout/CollectionsLayout";
import LoginSignUpLayout from "components/layout/LoginSignUpLayout";
import ToolsLayout from "components/layout/ToolsLayout";
import PublicCollectionPage from "features/PublicCollection";

import Landing from "views/Landing";
import NewsPage from "views/Newspage";
import NewsCategoryPage from "views/Search/NewsCategoryPage";
import ProfileIndexPage from "views/Profile/ProfileIndexPage";
import InfoDietPage from "views/Profile/InfoDietPage";
import SummarizerPage from "views/Tools/SummarizerPage";
import DomainCheckerPage from "views/Tools/DomainCheckerPage";
import MisinformationFingerprintsPage from "views/Tools/MisinformationFingerprintsPage";
import MisinformationSearchPage from "views/Tools/MisinformatinSearchPage";
import CollectionIndex from "views/Collections";
import Collection from "views/Collections/Collection";
import PublicIndexPage from "views/Collections/PublicIndex";
import ArchiveIndexPage from "views/Collections/ArchiveIndex";
import {
  SubmissionFoldersPage,
  SubmissionFolderDetailPage,
  SubmissionSnapshotPage,
} from "features/Submissions";
import Error404 from "views/404";
import SignupForm from "views/SignupForm";
import ForgotPassword from "views/ForgotPassword";
import Login from "views/Login";
import EmailConfirm from "views/EmailConfirm/EmailConfirm";
import EmailMessage from "views/EmailConfirm/EmailMessage";
import ResetPassword from "views/ResetPassword";
import OauthLogin from "views/MicrosoftLogin";
import CleverLoggedIn from "views/CleverLoggedIn";
import ClassLinkLogin from "views/ClasslinkLogin";
import MisinformationFingerprintsDetailsPage from "views/Tools/MisinformationFingerprintsDetailsPage";
import AdminPage from "views/AdminPage";
import SearchSourceSwitch from "views/Search/SearchSourceSwitch";
import SubscriptionPage from "views/Profile/Subscription/SubscriptionPage";
import IndividualSignUpPage from "views/Profile/Subscription/IndividualSignUpPage";
import PaymentStatusPage from "views/Profile/Subscription/PaymentStatus";
import { useUserData } from "features/User";
import PulseLayout from "components/layout/PulseLayout";
import PulseHomePage from "views/Search/PulseHomePage";
import PreferencePage from "views/Profile/PreferencePage";
import GroupUserManagement from "views/Profile/GroupManagement/UserManagement";
import AddUsersPage from "views/Profile/GroupManagement/AddUsers";
import AfpPage from "views/Search/AfpPage";
import GroupSignUpPage from "views/Profile/Subscription/GroupSignUpPage";
import FloatingHelpButton from "features/FloatingHelp/components/Button";
import SupportPage from "views/Profile/SupportPage";
import ReadingLevel from "views/Tools/ReadingLevel";
import { UserLicenseLevelModal, UserGradeLevelModal } from "features/Group";
import GroupAnalyticsPage from "views/Profile/GroupManagement/GroupAnalytics";
import NYCSSOLogin from "views/nyc-sso-login";
import FeatureDisabled from "components/FeatureDisabled";
import Engagement from "views/Profile/Publishers/Engagement";
import Comparision from "views/Profile/Publishers/Comparision";
import SoothRedirect from "components/base/SoothRedirect";
import { TourProvider } from "features/Tours";
import SignUpHomeSchoolPage from "views/SignUpHomeSchool";
import MicrosoftRedirect from "views/MicrosoftRedirect";
import { useSelector } from "react-redux";
import { enableUserGradeLevelModal, isgradneeded, showDatabaseSearch } from "config";
import Preferences from "views/Profile/GroupManagement/Preferences";
import GroupPreferences from "views/Profile/GroupManagement/GroupPreferences";

const SPLASH_STUCK_MS = 30000;

export default function Router() {
  const dispatch = useDispatch();
  const { hash } = useLocation();

  const {
    isSubscriptionExpired,
    getSubscriptionInfo,
    getUserInfo,
    updateReduxState,
    isSubscriptionLoading,
    isAuthenticated,
    userLoading,
  } = useUserData();

  const { publisherDetails } = useSelector(state => state.publisher);

  const logoutUserFromApp = () => {
    dispatch(logoutUser());
  };

  const getUserDetails = async () => {
    try {
      await getUserInfo();
      await getSubscriptionInfo();
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    setupInterceptorsTo(
      updateReduxState,
      logoutUserFromApp,
      isSubscriptionExpired
    );
  });

  const checkUrl = () => {
    if (isAuthenticated && localStorage.getItem("url")) {
      const url = localStorage.getItem("url");
      localStorage.removeItem("url");
      window.location = url;
    }
  };

  useEffect(() => {
    getUserDetails();
    checkUrl();
  }, [isAuthenticated]);

  // Watchdog: report when the SplashScreen has been up too long, so a
  // "blank screen" report has something to look up in client-logs.
  const showingSplash = userLoading || isSubscriptionLoading;
  useEffect(() => {
    if (!showingSplash) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      logClientEvent({
        source: "App/Startup/SplashScreenStuck",
        level: "Warning",
        message: `SplashScreen still showing after ${SPLASH_STUCK_MS}ms`,
        extra: {
          userLoading,
          isSubscriptionLoading,
          isAuthenticated,
          elapsedMs: Date.now() - startedAt,
        },
      });
    }, SPLASH_STUCK_MS);
    return () => clearTimeout(timer);
  }, [showingSplash]);

  useEffect(() => {
    const element = document.getElementById(hash.substring(1));
    if (element != null) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [hash]);

  return (
    <React.Fragment>
      {isgradneeded ? (
        enableUserGradeLevelModal && !userLoading && !isSubscriptionLoading && <UserGradeLevelModal />
      ) : (
        !userLoading && !isSubscriptionLoading && <UserLicenseLevelModal />
      )}
      {isAuthenticated && !isSubscriptionLoading && <FloatingHelpButton />}
      {userLoading || isSubscriptionLoading ? (
        <SplashScreen />
      ) : !isAuthenticated ? (
        <Routes>
          <Route path="/" element={<LoginSignUpLayout />}>
            <Route index element={<Login />} />
            <Route path="signin/microsoft" element={<OauthLogin />} />
            <Route path="signin/clever" element={<OauthLogin />} />
            <Route path="signin/classlink" element={<ClassLinkLogin />} />
            <Route path="signin/nycenet" element={<NYCSSOLogin />} />
            <Route path="signin" element={<MicrosoftRedirect />} />
            <Route path="signup" element={<SignupForm />} />
            <Route path="signup/homeschool" element={<SignUpHomeSchoolPage />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route
              path="SignupMessage"
              element={<EmailMessage type="signup" />}
            />
            <Route
              path="ResetPasswordMessage"
              element={<EmailMessage type="resetpassword" />}
            />
            <Route path="ConfirmEmail" element={<EmailConfirm />} />
            <Route path="ResetPassword" element={<ResetPassword />} />
            <Route path="*" element={<Navigate redirect to="/" />} />
          </Route>
        </Routes>
      ) : isSubscriptionExpired ? (
        <Routes>
          <Route path="/profile" element={<ProfileLayout />}>
            <Route index element={<ProfileIndexPage />} />
            <Route path="subscription">
              <Route index element={<SubscriptionPage />} />
              <Route
                path="individual-plan"
                element={<IndividualSignUpPage />}
              />
              <Route path="payment-status" element={<PaymentStatusPage />} />
              <Route path="group-plan" element={<GroupSignUpPage />} />
            </Route>

            <Route
              path="*"
              element={<Navigate redirect to="/profile/subscription" />}
            />
          </Route>
          <Route
            path="*"
            element={<Navigate redirect to="/profile/subscription" />}
          />
        </Routes>
      ) : (
        <Fragment>
          <TourProvider />
          <Routes>
            <Route index element={<Landing />} />
            <Route path="/signin/*" element={<Navigate redirect to="/" />} />
            <Route path="/signup/*" element={<Navigate redirect to="/" />} />

            <Route
              path="/ConfirmEmail"
              element={<Navigate redirect to="/" />}
            />
            <Route
              path="/ResetPassword"
              element={<Navigate redirect to="/" />}
            />
            <Route path="/afp-article/:afpId" element={<AfpPage />} />

            <Route path="/pulse" element={<PulseLayout />}>
              <Route index element={<PulseHomePage />} />
              <Route path="label/:category" element={<PulseHomePage />} />
              <Route
                path="label/:category/search/:search"
                element={<PulseHomePage />}
              />
              <Route
                path="breakout/:breakcategory"
                element={<PulseHomePage />}
              />
              <Route
                path="breakout/:breakcategory/:index"
                element={<PulseHomePage />}
              />
              <Route
                path="breakout/:breakcategory/search/:search"
                element={<PulseHomePage />}
              />
              <Route
                path="breakout/:breakcategory/:index/search/:search"
                element={<PulseHomePage />}
              />
              <Route path="search/:search" element={<PulseHomePage />} />
            </Route>

            <Route path="/collections" element={<CollectionsLayout />}>
              <Route index element={<CollectionIndex />} />
              <Route path="submissions" element={<SubmissionFoldersPage />} />
              <Route path="submissions/:id" element={<SubmissionFolderDetailPage />} />
              <Route
                path="submissions/:id/snapshot/:snapshotId"
                element={<SubmissionSnapshotPage />}
              />
              {/* An archived folder gets its own URL, the way an archived collection does. */}
              <Route
                path="submissions/archive/:id"
                element={<SubmissionFolderDetailPage />}
              />
              <Route
                path="submissions/archive/:id/snapshot/:snapshotId"
                element={<SubmissionSnapshotPage />}
              />
              <Route path=":id" element={<Collection />} />
              <Route path=":id/work-cited" element={<Collection />} />
              <Route path=":id/collection-notes" element={<Collection />} />
              <Route path=":id/teacher-submission/:submissionId" element={<Collection teacherMode />} />
              <Route path=":id/teacher-submission/:submissionId/work-cited" element={<Collection teacherMode />} />
              <Route path=":id/teacher-submission/:submissionId/collection-notes" element={<Collection teacherMode />} />
              <Route path=":id/teacher-submission/:submissionId/teacher-notes" element={<Collection teacherMode />} />
              <Route path=":id/teacher-notes" element={<Collection />} />
              <Route path=":id/work-cited/create" element={<Collection />} />
              <Route
                path=":id/work-cited/edit/:workcitedid"
                element={<Collection />}
              />
              <Route path="public" element={<PublicIndexPage />} />
              <Route path="public/:id" element={<PublicCollectionPage />} />
              <Route path="archive/:id" element={<Collection />} />
              <Route path="archive/:id/work-cited" element={<Collection />} />
              <Route
                path="archive/:id/collection-notes"
                element={<Collection />}
              />
            </Route>
            <Route path="/profile" element={<ProfileLayout />}>
              <Route index element={<ProfileIndexPage />} />
              <Route path="info-diet" element={<InfoDietPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="sooth-admin" element={<AdminPage />} />
              <Route path="preferences" element={<PreferencePage />} />
              <Route path="subscription">
                <Route index element={<SubscriptionPage />} />
                <Route
                  path="individual-plan"
                  element={<IndividualSignUpPage />}
                />
                <Route path="payment-status" element={<PaymentStatusPage />} />
                <Route path="group-plan" element={<GroupSignUpPage />} />
              </Route>
              <Route path="group">
                <Route
                  path="user-management"
                  element={<GroupUserManagement />}
                />
                <Route path="add-users" element={<AddUsersPage />} />
                <Route path="group-preferences" element={<GroupPreferences />} />
                <Route
                  path="integrations"
                  element={
                    showDatabaseSearch ? (
                      <Preferences />
                    ) : (
                      <Navigate to="/profile/group/user-management" replace />
                    )
                  }
                />
                <Route
                  path="preferences"
                  element={
                    <Navigate to="/profile/group/integrations" replace />
                  }
                />
                <Route path="analytics" element={<GroupAnalyticsPage />} />
                <Route
                  path="*"
                  element={
                    <Navigate redirect to="/profile/group/user-management" />
                  }
                />
              </Route>
              <Route path="publisher-analytics">
                <Route path="engagement" element={<Engagement />} />
                <Route path="benchmarking" element={<FeatureDisabled />} />
                {!publisherDetails?.[0]?.opt_Out &&
                  <Route path="comparison" element={<Comparision />} />
                }
                <Route path="reports" element={<FeatureDisabled />} />
                <Route
                  path="*"
                  element={
                    <Navigate
                      redirect
                      to="/profile/publisher-analytics/engagement"
                    />
                  }
                />
              </Route>

              <Route path="*" element={<Navigate redirect to="/profile" />} />
            </Route>
            <Route path="/tools" element={<ToolsLayout />}>
              <Route
                index
                element={<Navigate redirect to="/tools/summarizer" />}
              />
              <Route path="misinformation-search">
                <Route
                  index
                  element={
                    <Navigate redirect to="/tools/dubious-sources-search" />
                  }
                />
                <Route
                  path="*"
                  element={
                    <Navigate redirect to="/tools/dubious-sources-search" />
                  }
                />
              </Route>
              <Route path="summarizer" element={<FeatureDisabled />} />
              <Route path="domain-checker" element={<DomainCheckerPage />} />
              <Route
                path="domain-checker/:domainName"
                element={<DomainCheckerPage />}
              />
              {/* Redirects for Misinformation Fingerprints to False Claim Fingerprints */}
              <Route
                path="misinformation-fingerprints"
                element={<SoothRedirect to="../false-claim-fingerprints" />}
              />
              <Route
                path="misinformation-fingerprints/:fingerprintSearch"
                element={
                  <SoothRedirect to="../false-claim-fingerprints/:fingerprintSearch" />
                }
              />
              <Route
                path="misinformation-fingerprints/details/:id"
                element={
                  <SoothRedirect to="../false-claim-fingerprints/details/:id" />
                }
              />
              <Route
                path="false-claim-fingerprints"
                element={<MisinformationFingerprintsPage />}
              />
              <Route
                path="false-claim-fingerprints/:fingerprintSearch"
                element={<MisinformationFingerprintsPage />}
              />
              <Route
                path="false-claim-fingerprints/details/:id"
                element={<MisinformationFingerprintsDetailsPage />}
              />
              <Route
                path="dubious-sources-search"
                element={<MisinformationSearchPage />}
              />
              <Route
                path="dubious-sources-search/:misinformationsearch"
                element={<MisinformationSearchPage />}
              />
              <Route path="reading-level" element={<FeatureDisabled />} />
            </Route>

            {/* <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminPage />} />
          </Route> */}

            {/* Why is this here? */}
            <Route path="/signin/clever" element={<CleverLoggedIn />} />
            <Route path="/search/:search" element={<Layout />}>
              <Route index element={<NewsPage />} />
              <Route path=":source" element={<SearchSourceSwitch />} />
              <Route path=":source/:category" element={<NewsCategoryPage />} />
              <Route
                path=":source/:category/:academiaSearch"
                element={<NewsCategoryPage />}
              />
            </Route>

            <Route path="*" element={<Error404 />} />
          </Routes>{" "}
        </Fragment>
      )}
    </React.Fragment>
  );
}
